// T87: Rozliczenia (wypłaty). Lista pracowników z „do wypłaty" od ostatniego rozliczenia,
// zaznaczenie kilku osób → „Rozlicz do dnia X" → podsumowanie → potwierdzenie. Historia wypłat,
// cofnięcie (admin), eksport CSV do przelewów.
// T92 (08.10): bez pliku po każdym rozliczeniu — jeden plik dzienny ze wszystkimi rozliczonymi tego dnia.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Modal, PersonCell, RoleBadge, fullName, useToast, useSort, SortTh, workLabel, bonusLabel } from '../components/UI';
import { SpecTable, SettlementSpec, loadSpecs, PersonEventsModal } from '../components/PaySpec';
import api from '../utils/api';

const zl = (v) => `${(Number(v) || 0).toFixed(2).replace('.', ',')} zł`;
const fmt = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '—';
// Dzień wg czasu lokalnego (RRRR-MM-DD) — toISOString dawałby po północy jeszcze „wczoraj"
const dayOf = (d) => new Date(d).toLocaleDateString('sv-SE');
const todayKey = () => dayOf(new Date());
// T116: forma wypłaty (starsze rozliczenia: brak — do uzupełnienia w Historii)
const METHOD_LABEL = { TRANSFER: 'Przelew', CASH: 'Gotówka' };
const methodTxt = (m) => METHOD_LABEL[m] || 'nie podano';
const SORT = {
  employee: r => `${r.user.lastName} ${r.user.firstName}`,
  last: r => r.last?.upToDate || '',
  toPay: r => r.toPay.amount,
  pending: r => r.pending.amount,
};

function downloadCsv(name, rows) {
  const csv = '﻿' + rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/** T92: plik do przelewów za dzień — wszyscy rozliczeni tego dnia, jeden wiersz na osobę + suma. */
function downloadDay(day, list) {
  const byUser = {};
  for (const s of list) {
    const u = byUser[s.user.id] || (byUser[s.user.id] = { user: s.user, upTo: s.upToDate, hours: 0, daily: 0, bonus: 0, amount: 0, entries: 0, by: new Set(), notes: [], methods: new Set() });
    u.methods.add(methodTxt(s.payMethod));
    if (String(s.upToDate) > String(u.upTo)) u.upTo = s.upToDate;
    u.hours += Number(s.hoursTotal) || 0; u.daily += s.dailyCount || 0; u.bonus += Number(s.bonusAmount) || 0;
    u.amount += Number(s.amount) || 0; u.entries += s.entriesCount || 0;
    if (s.createdBy) u.by.add(fullName(s.createdBy));
    if (s.note) u.notes.push(s.note);
  }
  const people = Object.values(byUser).sort((a, b) => `${a.user.lastName} ${a.user.firstName}`.localeCompare(`${b.user.lastName} ${b.user.firstName}`, 'pl'));
  const n2 = (v) => v.toFixed(2).replace('.', ',');
  downloadCsv(`wyplaty_${day}.csv`, [
    ['Pracownik', 'Rozliczono do dnia', 'Godziny', 'Dniówki', 'Dodatki (zł)', 'Do wypłaty (zł)', 'Forma wypłaty', 'Wpisy', 'Rozliczył', 'Uwaga'],
    ...people.map(u => [fullName(u.user), fmt(u.upTo), String(u.hours).replace('.', ','), u.daily, n2(u.bonus), n2(u.amount), [...u.methods].join(' + '), u.entries, [...u.by].join(', '), u.notes.join(' / ')]),
    ['RAZEM', '', '', '', '', n2(people.reduce((a, u) => a + u.amount, 0)), '', '', `${people.length} os.`, ''],
    ...methodSums(list).map(([m, v]) => [`w tym: ${m}`, '', '', '', '', n2(v), m, '', '', '']),
  ]);
}

/** T116: suma kwot wg formy wypłaty — [['Przelew', 1200], ['Gotówka', 300]] (tylko niezerowe). */
function methodSums(list) {
  const out = {};
  for (const s of list) out[methodTxt(s.payMethod)] = (out[methodTxt(s.payMethod)] || 0) + (Number(s.amount) || 0);
  return ['Przelew', 'Gotówka', 'nie podano'].filter(k => out[k]).map(k => [k, out[k]]);
}

/** T116: eksport listy z Historii (z filtrem dnia i formy) — jeden wiersz na rozliczenie. */
function downloadHistory(name, list) {
  const n2 = (v) => (Number(v) || 0).toFixed(2).replace('.', ',');
  downloadCsv(name, [
    ['Pracownik', 'Rozliczono do dnia', 'Data rozliczenia', 'Kwota (zł)', 'Forma wypłaty', 'Godziny', 'Dniówki', 'Dodatki (zł)', 'Rozliczył', 'Uwaga'],
    ...list.map(s => [fullName(s.user), fmt(s.upToDate), fmt(s.createdAt), n2(s.amount), methodTxt(s.payMethod), String(s.hoursTotal).replace('.', ','), s.dailyCount, n2(s.bonusAmount), s.createdBy ? fullName(s.createdBy) : '', s.note || '']),
    ['RAZEM', '', '', n2(list.reduce((a, s) => a + (Number(s.amount) || 0), 0)), '', '', '', '', `${list.length} rozl.`, ''],
    ...methodSums(list).map(([m, v]) => [`w tym: ${m}`, '', '', n2(v), m, '', '', '', '', '']),
  ]);
}

/** T90: specyfikacja dnia — każdy rozliczony wpis w osobnym wierszu (za co), do archiwum. */
async function downloadDaySpec(day, list) {
  const specs = await loadSpecs(list.map(h => h.id));
  const n2 = (v) => (Number(v) || 0).toFixed(2).replace('.', ',');
  const rows = [['Pracownik', 'Rozliczono do dnia', 'Data pracy', 'Za co', 'Event', 'Od', 'Do', 'Godziny', 'Kwota (zł)', 'Opis']];
  for (const sp of specs) {
    for (const e of sp.entries) {
      rows.push([fullName(sp.user), fmt(sp.upToDate), fmt(e.date),
        e.kind === 'hours' ? workLabel(e.workType) : 'Dodatek: ' + bonusLabel(e.bonusType),
        e.event?.name || '', e.startTime || '', e.endTime || '',
        e.kind === 'hours' ? (e.daily ? 'dniówka' : String(e.hours).replace('.', ',')) : '',
        n2(e.amount), e.description || '']);
    }
  }
  rows.push(['RAZEM', '', '', '', '', '', '', '', n2(specs.reduce((a, sp) => a + (Number(sp.amount) || 0), 0)), '']);
  downloadCsv(`specyfikacja_${day}.csv`, rows);
}

export default function SettlementsPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('open');
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [sel, setSel] = useState({});
  const [onlyToPay, setOnlyToPay] = useState(true);
  const [preview, setPreview] = useState(null); // { upToDate, rows }
  const [upToDate, setUpToDate] = useState(todayKey());
  const [note, setNote] = useState('');
  const [methods, setMethods] = useState({}); // T116: { userId: 'CASH' } — domyślnie przelew
  const [methodFilter, setMethodFilter] = useState(''); // T116: '' | TRANSFER | CASH | NONE
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(todayKey()); // T92: wybrany dzień w Historii ('' = wszystkie)
  const [openSpec, setOpenSpec] = useState({}); // T90: rozwinięte „za co" (podgląd: userId, historia: id rozliczenia)
  const [person, setPerson] = useState(null);   // T91: karta „Eventy pracownika"
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.getSettlementSummary(), api.getSettlementHistory()]);
      setRows(s); setHistory(h);
    } catch (e) { show(e.message, 'error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = rows.filter(r => !onlyToPay || r.toPay.amount > 0 || r.pending.entriesCount > 0);
  const ss = useSort(visible, SORT, 'employee', 'asc');
  const chosen = visible.filter(r => sel[r.user.id]);
  const totalToPay = visible.reduce((s, r) => s + r.toPay.amount, 0);
  const totalPending = visible.reduce((s, r) => s + r.pending.amount, 0);

  const openPreview = async () => {
    if (!chosen.length) return;
    setBusy(true);
    try {
      const rows = await api.previewSettlement(chosen.map(r => r.user.id), upToDate);
      setPreview({ upToDate, rows });
      setOpenSpec(rows.length === 1 ? { [rows[0].user.id]: true } : {});
    }
    catch (e) { show(e.message, 'error'); }
    setBusy(false);
  };
  // zmiana daty w podglądzie → przelicz
  const changeDate = async (d) => {
    setUpToDate(d);
    if (!preview) return;
    try { setPreview({ upToDate: d, rows: await api.previewSettlement(chosen.map(r => r.user.id), d) }); }
    catch (e) { show(e.message, 'error'); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.createSettlement(chosen.map(x => x.user.id), preview.upToDate, note, methods);
      // T92: plik do przelewów pobiera się zbiorczo za cały dzień (przycisk „Pobierz plik za dziś")
      show(`${r.message || 'Rozliczono'} Plik do przelewów pobierzesz zbiorczo: „Pobierz plik za dziś".`);
      setPreview(null); setSel({}); setNote(''); setMethods({});
      load();
    } catch (e) { show(e.message, 'error'); }
    setBusy(false);
  };
  const revert = async (s) => {
    if (!window.confirm(`Cofnąć rozliczenie ${fullName(s.user)} do ${fmt(s.upToDate)} na ${zl(s.amount)}?\n\nWpisy wrócą do „do wypłaty" i znów będzie można je edytować.`)) return;
    try { const r = await api.revertSettlement(s.id); show(r.message); load(); } catch (e) { show(e.message, 'error'); }
  };
  const exportOpen = () => downloadCsv(`do_wyplaty_${todayKey()}.csv`, [
    ['Pracownik', 'Ostatnia wypłata (do dnia)', 'Do wypłaty (zł)', 'Godziny', 'Dniówki', 'Dodatki (zł)', 'Czeka na zatwierdzenie (zł)'],
    ...visible.map(r => [fullName(r.user), r.last?.upToDate || '', r.toPay.amount.toFixed(2), r.toPay.hoursTotal, r.toPay.dailyCount, r.toPay.bonusAmount.toFixed(2), r.pending.amount.toFixed(2)]),
  ]);

  // T92: rozliczenia wg dnia ich wykonania
  const today = todayKey();
  const ofDay = (d) => history.filter(h => dayOf(h.createdAt) === d);
  const todayList = ofDay(today);
  const sumOf = (list) => list.reduce((a, h) => a + (Number(h.amount) || 0), 0);
  const peopleOf = (list) => new Set(list.map(h => h.user.id)).size;
  const days = [...new Set(history.map(h => dayOf(h.createdAt)))].slice(0, 14);
  const byMethod = (h) => !methodFilter || (methodFilter === 'NONE' ? !h.payMethod : h.payMethod === methodFilter);
  const historyVisible = (day ? ofDay(day) : history).filter(byMethod);
  const changeMethod = async (s, m) => {
    try { await api.setSettlementMethod(s.id, m); show('Forma wypłaty zapisana'); load(); } catch (e) { show(e.message, 'error'); }
  };

  const selectable = ss.sorted.filter(r => r.toPay.amount > 0);
  const allOn = selectable.length > 0 && selectable.every(r => sel[r.user.id]);

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Rozliczenia (wypłaty)</h2>
        <div className="filter-pills">
          <button className={`filter-pill ${tab === 'open' ? 'active' : ''}`} onClick={() => setTab('open')}>Do wypłaty</button>
          <button className={`filter-pill ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>Historia ({history.length})</button>
        </div>
      </div>

      {tab === 'open' && (
        <>
          <div className="card" style={{ fontSize: 12, color: '#475569' }}>
            „Do wypłaty" = <b>zatwierdzone</b> godziny, dniówki i dodatki, jeszcze nierozliczone. Zaznacz osoby i kliknij
            „Rozlicz" — wszystkie ich zatwierdzone wpisy do wybranego dnia zostaną zamknięte (nie da się ich potem edytować).
            Wpisy czekające na zatwierdzenie przechodzą na następną wypłatę.
          </div>
          {todayList.length > 0 && (
            <div className="flex gap-8" style={{ alignItems: 'center', fontSize: 12, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, padding: '8px 12px' }}>
              <span>✓ Dziś rozliczono: <b>{peopleOf(todayList)} os.</b> na <b>{zl(sumOf(todayList))}</b></span>
              <button className="btn btn-success btn-sm" style={{ marginLeft: 'auto' }} onClick={() => downloadDay(today, todayList)}
                title="Jeden plik ze wszystkimi rozliczonymi dzisiaj — do przelewów">⬇ Pobierz plik za dziś</button>
            </div>
          )}
          <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center' }}>
            <div className="card stat-card"><div className="stat-label">Do wypłaty razem</div><div className="stat-value" style={{ color: '#059669' }}>{zl(totalToPay)}</div></div>
            <div className="card stat-card"><div className="stat-label">Czeka na zatwierdzenie</div><div className="stat-value" style={{ color: '#94A3B8' }}>{zl(totalPending)}</div></div>
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyToPay} onChange={e => setOnlyToPay(e.target.checked)} /> tylko osoby z czymś do wypłaty
            </label>
            <button className="btn btn-outline btn-sm" onClick={exportOpen} style={{ marginLeft: 'auto' }} title="Zestawienie, kto ile ma do wypłaty (przed rozliczeniem)">Pobierz zestawienie „do wypłaty"</button>
          </div>
          {chosen.length > 0 && (
            <div className="flex gap-8" style={{ alignItems: 'center', fontSize: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px' }}>
              <b>Zaznaczone: {chosen.length}</b> · {zl(chosen.reduce((s, r) => s + r.toPay.amount, 0))}
              <span style={{ marginLeft: 'auto' }}>Rozlicz do dnia</span>
              <input type="date" value={upToDate} max={todayKey()} onChange={e => setUpToDate(e.target.value)} style={{ fontSize: 12 }} />
              <button className="btn btn-primary btn-sm" onClick={openPreview} disabled={busy}>Rozlicz…</button>
            </div>
          )}
          <div className="card">
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr>
                  <th style={{ width: 28 }}><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? {} : Object.fromEntries(selectable.map(r => [r.user.id, true])))} title="Zaznacz wszystkich z kwotą do wypłaty" /></th>
                  <SortTh label="Pracownik" k="employee" sort={ss.sort} onSort={ss.toggle} />
                  <th>Rola / stanowisko</th>
                  <SortTh label="Ostatnia wypłata" k="last" sort={ss.sort} onSort={ss.toggle} />
                  <SortTh label="Do wypłaty" k="toPay" sort={ss.sort} onSort={ss.toggle} />
                  <th>W tym</th>
                  <SortTh label="Czeka na zatwierdzenie" k="pending" sort={ss.sort} onSort={ss.toggle} />
                </tr></thead>
                <tbody>
                  {ss.sorted.map(r => (
                    <tr key={r.user.id} style={sel[r.user.id] ? { background: '#EFF6FF' } : undefined}>
                      <td>{r.toPay.amount > 0 && <input type="checkbox" checked={!!sel[r.user.id]} onChange={() => setSel(s => ({ ...s, [r.user.id]: !s[r.user.id] }))} />}</td>
                      <td><button className="btn btn-ghost btn-sm" style={{ padding: 0, textAlign: 'left' }} title="Na jakich eventach był, godziny, wypłaty" onClick={() => setPerson(r.user)}><PersonCell name={fullName(r.user)} /></button></td>
                      <td><RoleBadge role={r.user.role} />{r.user.position && <div style={{ fontSize: 10, color: '#64748B' }}>{r.user.position}</div>}</td>
                      <td style={{ fontSize: 11 }}>{r.last ? <>do {fmt(r.last.upToDate)}<br /><span style={{ color: '#94A3B8' }}>{zl(r.last.amount)}</span></> : <span style={{ color: '#94A3B8' }}>jeszcze nie</span>}</td>
                      <td><b style={{ color: r.toPay.amount > 0 ? '#059669' : '#94A3B8' }}>{zl(r.toPay.amount)}</b></td>
                      <td style={{ fontSize: 10, color: '#64748B' }}>
                        {r.toPay.hoursTotal ? `${r.toPay.hoursTotal} h` : ''}{r.toPay.dailyCount ? ` · ${r.toPay.dailyCount} dniówek` : ''}{r.toPay.bonusAmount ? ` · dodatki ${zl(r.toPay.bonusAmount)}` : ''}
                        {!r.toPay.entriesCount && '—'}
                      </td>
                      <td style={{ color: '#94A3B8', fontSize: 11 }}>{r.pending.entriesCount ? `${zl(r.pending.amount)} (${r.pending.entriesCount} wp.)` : '—'}</td>
                    </tr>
                  ))}
                  {ss.sorted.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Nikt nie ma nic do wypłaty</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="flex gap-6 flex-wrap" style={{ alignItems: 'center', marginBottom: 10, fontSize: 12 }}>
            <span style={{ color: '#64748B' }}>Rozliczenia z dnia:</span>
            <button className={`filter-pill ${day === '' ? 'active' : ''}`} onClick={() => setDay('')}>Wszystkie</button>
            {days.map(d => (
              <button key={d} className={`filter-pill ${day === d ? 'active' : ''}`} onClick={() => setDay(d)}>
                {d === today ? 'Dziś' : fmt(d)} ({peopleOf(ofDay(d))})
              </button>
            ))}
            <input type="date" value={day} max={today} onChange={e => setDay(e.target.value)} style={{ fontSize: 12 }} title="Inny dzień" />
            {day && (
              <button className="btn btn-success btn-sm" style={{ marginLeft: 'auto' }} disabled={!historyVisible.length}
                onClick={() => downloadDay(day, historyVisible)} title="Jeden plik ze wszystkimi rozliczonymi tego dnia — do przelewów">
                ⬇ Pobierz plik za {day === today ? 'dziś' : fmt(day)} ({peopleOf(historyVisible)} os., {zl(sumOf(historyVisible))})
              </button>
            )}
            {day && historyVisible.length > 0 && (
              <button className="btn btn-outline btn-sm" title="Każdy rozliczony wpis w osobnym wierszu — za co (eventy, godziny, dodatki)"
                onClick={() => downloadDaySpec(day, historyVisible).catch(e => show(e.message, 'error'))}>⬇ Specyfikacja (za co)</button>
            )}
          </div>
          <div className="flex gap-6 flex-wrap" style={{ alignItems: 'center', marginBottom: 10, fontSize: 12 }}>
            <span style={{ color: '#64748B' }}>Forma wypłaty:</span>
            {[['', 'Wszystkie'], ['TRANSFER', '🏦 Przelew'], ['CASH', '💵 Gotówka'], ['NONE', 'Nie podano']].map(([v, l]) => (
              <button key={v} className={`filter-pill ${methodFilter === v ? 'active' : ''}`} onClick={() => setMethodFilter(v)}>{l}</button>
            ))}
            <span style={{ marginLeft: 8 }}>
              Razem <b>{zl(sumOf(historyVisible))}</b>
              {methodSums(historyVisible).map(([m, v]) => <span key={m} style={{ color: m === 'Gotówka' ? '#B45309' : '#64748B' }}> · {m.toLowerCase()} <b>{zl(v)}</b></span>)}
            </span>
            <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} disabled={!historyVisible.length}
              onClick={() => downloadHistory(`historia_wyplat_${day || 'wszystkie'}${methodFilter ? '_' + methodFilter.toLowerCase() : ''}.csv`, historyVisible)}
              title="Lista widocznych rozliczeń z formą wypłaty — do Excela">⬇ Eksport listy (Excel)</button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Do dnia</th><th>Kwota</th><th>Forma</th><th>W tym</th><th>Rozliczył</th><th>Uwaga</th>{isAdmin && <th></th>}</tr></thead>
              <tbody>
                {historyVisible.map(s => (
                  <React.Fragment key={s.id}>
                  <tr>
                    <td><button className="btn btn-ghost btn-sm" style={{ padding: 0, textAlign: 'left' }} title="Na jakich eventach był, godziny, wypłaty" onClick={() => setPerson(s.user)}><PersonCell name={fullName(s.user)} /></button></td>
                    <td>{fmt(s.upToDate)}</td>
                    <td><b>{zl(s.amount)}</b></td>
                    <td>
                      <select value={s.payMethod || ''} onChange={e => changeMethod(s, e.target.value)} title="Forma wypłaty — można poprawić"
                        style={{ fontSize: 11, padding: '2px 4px', color: s.payMethod === 'CASH' ? '#B45309' : s.payMethod ? undefined : '#DC2626' }}>
                        {!s.payMethod && <option value="" disabled>— wybierz —</option>}
                        <option value="TRANSFER">🏦 Przelew</option>
                        <option value="CASH">💵 Gotówka</option>
                      </select>
                    </td>
                    <td style={{ fontSize: 10, color: '#64748B' }}>{s.hoursTotal} h{s.dailyCount ? ` · ${s.dailyCount} dniówek` : ''}{s.bonusAmount ? ` · dodatki ${zl(s.bonusAmount)}` : ''} · {s.entriesCount} wp.
                      <div><button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => setOpenSpec(o => ({ ...o, [s.id]: !o[s.id] }))}>{openSpec[s.id] ? '▾' : '▸'} za co</button></div>
                    </td>
                    <td style={{ fontSize: 11 }}>{s.createdBy ? fullName(s.createdBy) : '—'}<br /><span style={{ color: '#94A3B8' }}>{fmt(s.createdAt)}</span></td>
                    <td style={{ fontSize: 11, color: '#64748B' }}>{s.note || '—'}</td>
                    {isAdmin && <td><button className="btn btn-ghost btn-sm" onClick={() => revert(s)} title="Cofnij rozliczenie">↩ cofnij</button></td>}
                  </tr>
                  {openSpec[s.id] && <tr><td colSpan={8} style={{ background: '#F8FAFC' }}><SettlementSpec id={s.id} bare /></td></tr>}
                  </React.Fragment>
                ))}
                {historyVisible.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>{day ? 'Tego dnia nikogo nie rozliczono' : 'Brak rozliczeń'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview && (
        <Modal title={`Rozliczenie do ${fmt(preview.upToDate)}`} onClose={() => setPreview(null)} wide dirty={!!note || Object.keys(methods).length > 0}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="flex gap-8" style={{ alignItems: 'center', fontSize: 12 }}>
              Rozlicz do dnia <input type="date" value={preview.upToDate} max={todayKey()} onChange={e => changeDate(e.target.value)} />
            </div>
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Godziny</th><th>Dniówki</th><th>Dodatki</th><th>Razem</th><th>Forma wypłaty</th><th>Zostaje (czeka)</th></tr></thead>
              <tbody>
                {preview.rows.map(r => (
                  <React.Fragment key={r.user.id}>
                  <tr style={r.entriesCount ? undefined : { opacity: 0.5 }}>
                    <td>{fullName(r.user)}
                      {r.entriesCount > 0 && <div><button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => setOpenSpec(o => ({ ...o, [r.user.id]: !o[r.user.id] }))}>{openSpec[r.user.id] ? '▾' : '▸'} za co ({r.entries?.length || 0})</button></div>}
                    </td>
                    <td>{r.hoursTotal} h</td><td>{r.dailyCount}</td><td>{zl(r.bonusAmount)}</td>
                    <td><b>{r.entriesCount ? zl(r.amount) : 'nic do tej daty'}</b></td>
                    <td>{r.entriesCount > 0 && (
                      <select value={methods[r.user.id] || 'TRANSFER'} onChange={e => setMethods(m => ({ ...m, [r.user.id]: e.target.value }))} style={{ fontSize: 11, padding: '2px 4px' }}>
                        <option value="TRANSFER">🏦 Przelew</option><option value="CASH">💵 Gotówka</option>
                      </select>
                    )}</td>
                    <td style={{ color: r.pendingLeft ? '#D97706' : '#94A3B8' }}>{r.pendingLeft ? `⚠ ${r.pendingLeft} wp. niezatwierdzonych` : '—'}</td>
                  </tr>
                  {openSpec[r.user.id] && r.entriesCount > 0 && <tr><td colSpan={7} style={{ background: '#F8FAFC' }}><SpecTable entries={r.entries} /></td></tr>}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div className="flex gap-8 flex-wrap" style={{ fontSize: 13, alignItems: 'center' }}>
              <b>Razem do wypłaty: {zl(preview.rows.reduce((s, r) => s + r.amount, 0))}</b>
              {(() => { const cash = preview.rows.filter(r => r.entriesCount && methods[r.user.id] === 'CASH').reduce((s, r) => s + r.amount, 0);
                return cash > 0 ? <span style={{ color: '#B45309', fontSize: 12 }}>w tym gotówką {zl(cash)}</span> : null; })()}
              {preview.rows.filter(r => r.entriesCount).length > 1 && (
                <span style={{ marginLeft: 'auto', fontSize: 11 }}>Wszystkim:{' '}
                  <button className="btn btn-ghost btn-sm" onClick={() => setMethods(Object.fromEntries(preview.rows.map(r => [r.user.id, 'TRANSFER'])))}>🏦 przelew</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setMethods(Object.fromEntries(preview.rows.map(r => [r.user.id, 'CASH'])))}>💵 gotówka</button>
                </span>
              )}
            </div>
            {preview.rows.some(r => r.pendingLeft) && (
              <div style={{ fontSize: 11, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 8px' }}>
                Niektóre wpisy do tej daty <b>czekają na zatwierdzenie</b> — nie wejdą do tej wypłaty, przejdą na następną.
                Jeśli mają wejść teraz, najpierw je zatwierdź w Godzinach.
              </div>
            )}
            <div className="input-group"><label>Uwaga (opcjonalnie)</label><input value={note} onChange={e => setNote(e.target.value)} placeholder="np. wypłacone 07.10 w biurze" maxLength={300} /></div>
            <div style={{ fontSize: 11, color: '#64748B' }}>Po zatwierdzeniu rozliczone wpisy są zablokowane. Plik do przelewów pobierzesz zbiorczo na koniec dnia — „Pobierz plik za dziś" (wszyscy rozliczeni danego dnia w jednym pliku).</div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setPreview(null)}>Anuluj</button>
              <button className="btn btn-primary" onClick={confirm} disabled={busy || !preview.rows.some(r => r.entriesCount)}>{busy ? 'Rozliczanie…' : 'Zatwierdź rozliczenie'}</button>
            </div>
          </div>
        </Modal>
      )}
      {person && <PersonEventsModal userId={person.id} name={fullName(person)} onClose={() => setPerson(null)} />}
      {ToastComponent}
    </div>
  );
}
